/**
 * useAudioReactivity Hook - Exposes real-time audio frequency data
 * Connects to microphone via Web Audio API AnalyserNode
 * Used by the particle system to pulse with voice activity
 */

import { useRef, useCallback, useState } from 'react';

export interface AudioSample {
  /** Overall amplitude 0-1 */
  amplitude: number;
  /** Bass frequency energy 0-1 (20-250Hz) */
  bass: number;
  /** Mid frequency energy 0-1 (250-4000Hz) */
  mid: number;
  /** Treble frequency energy 0-1 (4000-20000Hz) */
  treble: number;
}

const EMPTY_SAMPLE: AudioSample = { amplitude: 0, bass: 0, mid: 0, treble: 0 };

/**
 * Hook that provides real-time audio frequency data from the microphone.
 * Call `start()` to begin capturing, `stop()` to release the mic.
 * Call `sample()` each animation frame to get current frequency data.
 */
export function useAudioReactivity() {
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const contextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);

  const start = useCallback(async () => {
    if (contextRef.current) return; // Already running

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const ctx = new AudioContext();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;

      const source = ctx.createMediaStreamSource(stream);
      source.connect(analyser);

      contextRef.current = ctx;
      analyserRef.current = analyser;
      streamRef.current = stream;
      dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);

      setEnabled(true);
      setError(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Microphone access denied';
      setError(msg);
      console.warn('[useAudioReactivity] Failed to start:', msg);
    }
  }, []);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (contextRef.current) {
      contextRef.current.close();
      contextRef.current = null;
    }
    analyserRef.current = null;
    dataArrayRef.current = null;
    setEnabled(false);
  }, []);

  /**
   * Sample current audio frequency data. Call once per animation frame.
   * Returns normalized values 0-1 for amplitude, bass, mid, treble.
   */
  const sample = useCallback((): AudioSample => {
    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;
    if (!analyser || !dataArray) return EMPTY_SAMPLE;

    analyser.getByteFrequencyData(dataArray);

    const binCount = dataArray.length; // 128 bins for fftSize=256
    const sampleRate = contextRef.current?.sampleRate ?? 44100;
    const binWidth = sampleRate / (analyser.fftSize); // ~172Hz per bin at 44100

    // Frequency band boundaries in bin indices
    const bassEnd = Math.min(Math.floor(250 / binWidth), binCount);
    const midEnd = Math.min(Math.floor(4000 / binWidth), binCount);

    let bassSum = 0;
    let midSum = 0;
    let trebleSum = 0;
    let totalSum = 0;

    for (let i = 0; i < binCount; i++) {
      const val = dataArray[i];
      totalSum += val;
      if (i < bassEnd) bassSum += val;
      else if (i < midEnd) midSum += val;
      else trebleSum += val;
    }

    const normalize = (sum: number, count: number) =>
      count > 0 ? Math.min(sum / (count * 255), 1) : 0;

    return {
      amplitude: normalize(totalSum, binCount),
      bass: normalize(bassSum, bassEnd),
      mid: normalize(midSum, midEnd - bassEnd),
      treble: normalize(trebleSum, binCount - midEnd),
    };
  }, []);

  return { enabled, error, start, stop, sample };
}
